import { proxy, subscribe, useSnapshot } from 'valtio'
import { devtools } from 'valtio/utils'

import { clearStore } from '@/lib/valtio/reset-store'
import { parseCellSnapshot } from '@/utils/parseSnapshotWrappers'

import type { MaybeCell } from '~shared/types/Cell'

import { getCellActions } from '../cell/cell.actions'
import { useCellKey } from '../cell/cell.context'
import type { CellKey } from '../cell/cell.schema'
import { makeCellKey } from '../cell/cell.schema'

import type { DocumentChange } from 'firebase/firestore'

type Cells = Record<CellKey, MaybeCell>

/**
 * Store for `rundown/cells` subcollection.
 *
 * @remarks
 *   Should be treated as read-only, mutations happen through API.
 *   That is why it is not exported, so it can only be modified inside module.
 *
 * @warning The Firestore listener must stay the ONLY writer. Never write an API
 * response back in as an optimistic update. That is a second writer racing the
 * listener, and an older snapshot arriving after a newer PATCH response silently
 * reverts the user's edit.
 */
const cellsStore = proxy<Cells>({})

export const cellsStoreActions = {
  /**
   * Syncs the local store with snapshot from firestore listener.
   */
  processDocChanges(changes: DocumentChange[]) {
    changes.forEach((change) => {
      const cell = parseCellSnapshot(change.doc)

      const cellKey = makeCellKey(cell.cueId, cell.columnId)

      // Delta: removed
      if (change.type === 'removed') {
        delete cellsStore[cellKey]
        return
      }

      // Delta: added | modified
      const existing = cellsStore[cellKey]
      if (existing) {
        // Object.assign does the merge in place and proxy identity stays stable.
        // Note: If cell documents can have fields removed (not just nulled),
        // then we probably need a small helper that can sync the stored cells fields
        // and deletes fields that were removed in the document.
        Object.assign(existing, cell)
      } else {
        cellsStore[cellKey] = cell as MaybeCell
      }
    })
  },

  /**
   * Empties the store. Called when leaving a rundown.
   */
  resetStore() {
    clearStore(cellsStore)
  },
}

/**
 * Look up a cell in `cellsStore` directly.
 *
 * @returns undefined if the cell is sparse or key doesn't match any cells
 */
export function getCell(cellKey: CellKey): MaybeCell {
  return cellsStore[cellKey]
}

/**
 * Wait until the listener has delivered a cell at or after a given version.
 *
 * @param cellKey Cell to watch
 * @param updatedAtMs Version to wait for, as ms epoch
 * @param timeoutMs Give up after this long
 *
 * @returns `true` once caught up, `false` on timeout
 */
export function waitForCellVersion(
  cellKey: CellKey,
  updatedAtMs: number,
  timeoutMs: number,
): Promise<boolean> {
  const isCaughtUp = () =>
    (cellsStore[cellKey]?.updatedAt?.getTime() ?? 0) >= updatedAtMs

  if (isCaughtUp()) return Promise.resolve(true)

  return new Promise((resolve) => {
    const finish = (caughtUp: boolean) => {
      clearTimeout(timeout)
      unsubscribe()
      resolve(caughtUp)
    }

    const unsubscribe = subscribe(cellsStore, () => {
      if (isCaughtUp()) finish(true)
    })

    const timeout = setTimeout(() => finish(false), timeoutMs)
  })
}

// =============== HOOKS

export function useCellSnapshot(): MaybeCell {
  const cellKey = useCellKey()

  const snap = useSnapshot(cellsStore)

  return snap[cellKey]
}

export function useCellActions() {
  const cellKey = useCellKey()

  const cellActions = getCellActions(cellKey)

  return cellActions
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(cellsStore, { name: 'cellsStore', enabled: true })
}
// #endregion
